import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { PRODUCT_META, PRODUCT_ORDER, HIDDEN_FROM_GRID } from "@/data/products";
import { useReveal } from "@/hooks/useReveal";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import HolidayBanner from "./HolidayBanner";
import BundleStrip from "./BundleStrip";
import DetailGallery from "./DetailGallery";
import { daysUntilChristmas } from "@/lib/christmas";

function formatNZD(amount) {
  return new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function ProductCard({ product, onOpen }) {
  const meta = PRODUCT_META[product.id];
  const ref = useReveal();
  const isChristmas = product.id === "christmas_stocking";
  const xmas = isChristmas ? daysUntilChristmas() : null;
  const showCountdown = isChristmas && xmas && !xmas.passed;
  const soldOut = product.in_stock === false;

  return (
    <article
      ref={ref}
      className="reveal group relative flex flex-col cursor-pointer"
      data-testid={`product-card-${product.id}`}
      style={{ transitionDelay: "60ms" }}
      onClick={() => onOpen(product.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(product.id);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${meta.title}`}
    >
      <div
        className={
          "relative aspect-[4/5] overflow-hidden rounded-[24px] bg-[#FFFDF9] shadow-[0_1px_0_rgba(54,41,42,0.04),0_18px_40px_-24px_rgba(54,41,42,0.18)] transition-shadow duration-500 " +
          (soldOut
            ? "grayscale-[.35]"
            : "group-hover:shadow-[0_1px_0_rgba(54,41,42,0.04),0_28px_60px_-24px_rgba(54,41,42,0.28)]")
        }
        style={
          meta.imageFit === "contain" && meta.imageBg
            ? { backgroundColor: meta.imageBg }
            : undefined
        }
      >
        {meta.image ? (
          <img
            src={meta.image}
            alt={meta.imageAlt}
            className={
              "absolute inset-0 w-full h-full transition-transform duration-[900ms] ease-out group-hover:scale-[1.06] " +
              (meta.imageFit === "contain"
                ? "object-contain p-4"
                : "object-cover")
            }
            loading="lazy"
          />
        ) : (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
            style={{
              background:
                "linear-gradient(160deg, #F5DFE1 0%, #F9E9DE 60%, #FBEFE6 100%)",
            }}
          >
            <div className="font-serif-me italic text-3xl text-[#7A4E5A]">
              Me Amor
            </div>
          </div>
        )}

        {/* darkening gradient for legibility */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#36292A]/55 via-[#36292A]/10 to-transparent" />

        {/* countdown pill for Christmas Stocking */}
        {showCountdown && !soldOut && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="absolute top-14 left-3 rounded-full bg-[#B23A48] text-white px-3 py-1.5 shadow-[0_8px_24px_-8px_rgba(178,58,72,0.6)]"
            data-testid="christmas-countdown-pill"
          >
            <div className="flex items-baseline gap-1.5">
              <span className="font-serif-me text-base leading-none">
                {xmas.days}
              </span>
              <span className="text-[9px] tracking-[0.24em] uppercase leading-none">
                {xmas.days === 1 ? "sleep" : "sleeps"} to go
              </span>
            </div>
          </motion.div>
        )}

        {/* top row: tagline + optional badge */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          <span className="max-w-[65%] truncate bg-[#FFFDF9]/90 backdrop-blur rounded-full px-2.5 py-1 text-[9px] tracking-[0.24em] uppercase text-[#36292A]">
            {meta.tagline}
          </span>
          {soldOut ? (
            <span
              className="max-w-[50%] truncate bg-[#7D6B6D] text-white rounded-full px-2.5 py-1 text-[9px] tracking-[0.24em] uppercase"
              data-testid={`product-badge-${product.id}`}
            >
              Sold out
            </span>
          ) : (
            meta.badge && (
              <span
                className="max-w-[45%] truncate bg-[#36292A] text-white rounded-full px-2.5 py-1 text-[9px] tracking-[0.24em] uppercase"
                data-testid={`product-badge-${product.id}`}
              >
                {meta.badge}
              </span>
            )
          )}
        </div>

        {/* bottom overlay caption */}
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 text-white">
          <h3 className="font-serif-me text-lg sm:text-xl leading-[1.15] drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)] line-clamp-2">
            {meta.title}
          </h3>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span
              className="rounded-full px-3 py-1 text-[11px] font-medium tracking-wide text-white"
              style={{ backgroundColor: soldOut ? "#7D6B6D" : meta.accent }}
              data-testid={`product-price-${product.id}`}
            >
              {soldOut ? "Sold out" : formatNZD(product.amount)}
            </span>
            <span
              className="flex items-center gap-1 text-[9px] tracking-[0.28em] uppercase text-white/85 opacity-0 translate-x-1 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0"
              data-testid={`product-view-details-${product.id}`}
            >
              {soldOut ? "Back soon" : "What's inside"}
              <span aria-hidden>→</span>
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

function NotifyMeForm({ productId, productName }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | done | already
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setError("Please enter a valid email.");
      return;
    }
    try {
      setStatus("submitting");
      setError("");
      const res = await api.post(`/products/${productId}/notify`, {
        product_id: productId,
        email: trimmed,
      });
      if (res.data?.already_in_stock) {
        toast.success(`${productName} is back in stock — grab it now!`);
        setStatus("done");
      } else if (res.data?.already_on_waitlist) {
        setStatus("already");
      } else {
        setStatus("done");
      }
    } catch (e2) {
      console.error(e2);
      setStatus("idle");
      setError("Couldn't join the waitlist. Please try again.");
    }
  };

  if (status === "done" || status === "already") {
    return (
      <div
        className="mt-8 rounded-2xl bg-[#F1E4DF] border border-[#E8D7D0] p-5 text-center"
        data-testid={`notify-me-success-${productId}`}
      >
        <div className="font-serif-me text-lg text-[#36292A]">
          {status === "already" ? "You're already on the list ✿" : "You're on the list ✿"}
        </div>
        <p className="mt-2 text-sm text-[#7D6B6D]">
          We&apos;ll email <strong>{email}</strong> the moment {productName}{" "}
          is back in stock.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-8 rounded-2xl bg-[#F9F6F0] border border-[#E8D7D0] p-5"
      data-testid={`notify-me-form-${productId}`}
    >
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#C48B9F]">
        Currently sold out
      </div>
      <div className="mt-1 font-serif-me text-lg text-[#36292A]">
        Let me know when it&apos;s back
      </div>
      <p className="mt-1 text-xs text-[#7D6B6D]">
        Drop your email and you&apos;ll be first to hear.
      </p>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          placeholder="you@email.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          className="flex-1 rounded-full border border-[#E8D7D0] bg-white px-4 py-2.5 text-sm text-[#36292A] focus:outline-none focus:border-[#C48B9F]"
          data-testid={`notify-me-email-${productId}`}
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="me-btn-primary justify-center"
          data-testid={`notify-me-submit-${productId}`}
        >
          {status === "submitting" ? "Adding…" : "Notify me"}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-[11px] text-[#B23A48]" data-testid={`notify-me-error-${productId}`}>
          {error}
        </p>
      )}
    </form>
  );
}

function ProductDetail({
  product,
  meta,
  subscription,
  onAdd,
  onSubscribe,
  subscribing,
  onClose,
}) {
  if (!product || !meta) return null;
  const isPenPal = product.id === "pen_pal" && subscription;
  const soldOut = product.in_stock === false;

  return (
    <div
      className="flex flex-col md:grid md:grid-cols-[1.05fr_1fr] md:items-start max-h-[92vh] overflow-y-auto overflow-x-hidden bg-[#FFFDF9] me-scroll"
      data-testid={`product-detail-${product.id}`}
    >
      {/* left: gallery — carousel when meta.gallery has multiple slides,
          otherwise a single static image */}
      <DetailGallery
        slides={
          meta.gallery && meta.gallery.length
            ? meta.gallery
            : meta.image
              ? [{ src: meta.image, alt: meta.imageAlt }]
              : []
        }
        imageFit={meta.imageFit}
        imageBg={meta.imageBg}
        tagline={meta.tagline}
        badge={meta.badge}
        testId={`product-detail-gallery-${product.id}`}
      />

      {/* right: details */}
      <div className="relative p-6 sm:p-8 md:p-10">
        <span className="text-[11px] tracking-[0.3em] uppercase text-[#C48B9F]">
          {meta.tagline}
        </span>
        <h3
          className="mt-2 font-serif-me text-3xl sm:text-4xl text-[#36292A] leading-[1.05]"
          data-testid={`product-detail-title-${product.id}`}
        >
          {meta.title}
        </h3>
        {meta.subtitle && (
          <p className="mt-2 font-serif-me italic text-[#C48B9F] text-lg">
            {meta.subtitle}
          </p>
        )}

        <div className="mt-4 flex items-baseline gap-3">
          <span
            className="rounded-full px-4 py-1.5 text-sm font-medium tracking-wide text-white"
            style={{ backgroundColor: soldOut ? "#7D6B6D" : meta.accent }}
          >
            {soldOut ? "Sold out" : formatNZD(product.amount)}
          </span>
          {!soldOut && isPenPal && (
            <span className="text-xs tracking-[0.24em] uppercase text-[#7D6B6D]">
              or {formatNZD(subscription.amount)} / season
            </span>
          )}
        </div>

        <p className="mt-5 text-sm text-[#7D6B6D] leading-relaxed">
          {meta.intro || product.description}
        </p>

        <div className="mt-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#7D6B6D] pb-2 border-b border-[#E8D7D0]">
            What&apos;s inside
          </div>
          <ul
            className="mt-4 space-y-3"
            data-testid={`product-detail-whats-inside-${product.id}`}
          >
            {(meta.whats_inside || []).map((item, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 + i * 0.05, duration: 0.35 }}
                className="flex items-start gap-3 text-sm"
              >
                <span
                  className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: meta.accent }}
                />
                <div>
                  <span className="font-medium text-[#36292A]">
                    {item.label}
                  </span>
                  <span className="text-[#7D6B6D]"> — {item.value}</span>
                </div>
              </motion.li>
            ))}
          </ul>
          {meta.footnote && (
            <p className="mt-5 text-[11px] italic text-[#7D6B6D] leading-relaxed">
              {meta.footnote}
            </p>
          )}
        </div>

        {soldOut ? (
          <NotifyMeForm productId={product.id} productName={meta.title} />
        ) : isPenPal ? (
          <div className="mt-8 flex flex-col gap-3">
            <button
              className="me-btn-primary justify-center"
              onClick={onSubscribe}
              disabled={subscribing}
              data-testid={`product-detail-subscribe-${product.id}`}
            >
              {subscribing
                ? "Redirecting…"
                : `Subscribe · ${formatNZD(subscription.amount)} / season`}
              <span aria-hidden>→</span>
            </button>
            <button
              className="me-btn-ghost justify-center"
              onClick={() => {
                onAdd(product.id);
                onClose();
              }}
              data-testid={`product-detail-add-${product.id}`}
            >
              Add one-off · {formatNZD(product.amount)}
            </button>
            <p className="text-[10px] tracking-[0.24em] uppercase text-[#C48B9F] text-center">
              Save {subscription.discount_percent}% · billed every 3 months
            </p>
          </div>
        ) : (
          <button
            className="mt-8 me-btn-primary w-full justify-center"
            onClick={() => {
              onAdd(product.id);
              onClose();
            }}
            data-testid={`product-detail-add-${product.id}`}
          >
            Add to basket · {formatNZD(product.amount)}
            <span aria-hidden>+</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default function ProductGrid() {
  const [products, setProducts] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [openId, setOpenId] = useState(null);
  const headingRef = useReveal();
  const { addItem } = useCart();

  useEffect(() => {
    (async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          api.get("/products"),
          api.get("/pen-pal/subscription"),
        ]);
        const list = pRes.data.products || [];
        list.sort(
          (a, b) => PRODUCT_ORDER.indexOf(a.id) - PRODUCT_ORDER.indexOf(b.id)
        );
        setProducts(list);
        setSubscription(sRes.data);      } catch (e) {
        console.error(e);
        toast.error("Couldn't load the boxes. Please refresh.");
      }
    })();
  }, []);

  const handleAdd = (productId) => {
    addItem(productId, 1);
    const meta = PRODUCT_META[productId];
    toast.success(`${meta?.title || "Box"} added to your basket.`);
  };

  const handleSubscribe = async () => {
    try {
      setSubscribing(true);
      const res = await api.post("/pen-pal/subscribe", {
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't start subscription. Please try again.");
      setSubscribing(false);
    }
  };

  const openProduct = products.find((p) => p.id === openId);
  const openMeta = openId ? PRODUCT_META[openId] : null;

  return (
    <section id="boxes" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <HolidayBanner
          onShop={() => {
            const el = document.querySelector(
              "[data-testid='product-card-christmas_stocking']"
            );
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              setTimeout(() => setOpenId("christmas_stocking"), 450);
            } else {
              setOpenId("christmas_stocking");
            }
          }}
        />

        <div
          ref={headingRef}
          className="reveal flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14"
          data-testid="products-heading"
        >
          <div>
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#C48B9F]">
              Our Boxes
            </span>
            <h2 className="mt-3 font-serif-me text-4xl sm:text-5xl text-[#36292A] max-w-2xl leading-[1.05]">
              A little more{" "}
              <em className="italic text-[#C48B9F] font-normal">love</em>,
              <br />
              for every occasion.
            </h2>
          </div>
          <p className="text-sm text-[#7D6B6D] max-w-sm">
            Tap a box to see what&apos;s inside. Prices in NZD · secure Stripe
            checkout · recyclable packaging.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 lg:gap-6">
          {products
            .filter((p) => p.id !== "butterfly_stocking_bundle")
            .map((p) => (
              <ProductCard key={p.id} product={p} onOpen={setOpenId} />
            ))}
        </div>

        <BundleStrip
          product={products.find((p) => p.id === "butterfly_stocking_bundle")}
        />
      </div>

      <Dialog open={!!openId} onOpenChange={(v) => !v && setOpenId(null)}>
        <DialogContent
          className="p-0 gap-0 border-none max-w-none sm:max-w-3xl md:max-w-4xl w-[calc(100vw-24px)] sm:w-auto rounded-[20px] overflow-hidden [&>button.absolute]:z-20 [&>button.absolute]:bg-white/95 [&>button.absolute]:backdrop-blur [&>button.absolute]:rounded-full [&>button.absolute]:p-2 [&>button.absolute]:opacity-100 [&>button.absolute]:shadow-md"
        >
          <DialogTitle className="sr-only">
            {openMeta?.title || "Product details"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {openMeta?.subtitle || openMeta?.intro || "Product details and what's inside."}
          </DialogDescription>
          <AnimatePresence mode="wait">
            {openProduct && openMeta && (
              <motion.div
                key={openProduct.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <ProductDetail
                  product={openProduct}
                  meta={openMeta}
                  subscription={
                    openProduct.id === "pen_pal" ? subscription : null
                  }
                  onAdd={handleAdd}
                  onSubscribe={handleSubscribe}
                  subscribing={subscribing}
                  onClose={() => setOpenId(null)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </section>
  );
}
